import { useState, useEffect } from "react";
import { User, Loader2, Plus } from "lucide-react";
import toast from "react-hot-toast";
import type { DoctorListItem, OrderDetailsResponse } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import DoctorSelectModal from "./DoctorSelectModal";
import { collection, doc, setDoc } from "firebase/firestore";
import { firestore as db } from "../../firebase/firebase";

interface DoctorAssignFlowProps {
  orderDetails: OrderDetailsResponse;
  onSuccess: () => void;
}

const DoctorAssignFlow: React.FC<DoctorAssignFlowProps> = ({ orderDetails, onSuccess }) => {
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListItem | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [showDoctorModal, setShowDoctorModal] = useState(false);

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async (name: string = "") => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getDoctorListing(name);
      if (response.success) setDoctors(response.data);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedDoctor) return;
    try {
      setAssigning(true);
      const response = await AdminOrderManagementService.bookDoctorAppointment(
        orderDetails.orderId,
        orderDetails.orderedFor || orderDetails.patientId,
        selectedDoctor.id
      );
      if (response.success) {
        const appointmentId = response.appointmentId || "";
        if (appointmentId) {
          const docRef = doc(
            collection(db, "patientPrescriptionAvailability"),
            appointmentId
          );

          const payloadFirebase = {
            age: orderDetails.orderedForAge || orderDetails.patientAge || "",
            appointmentDate: new Date().toISOString(),
            appointmentId: appointmentId,
            appointmentTime: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
            callStatus: "pending",
            consultationType: "Prescription",
            dhrId: orderDetails.orderNumber || "",
            doctorId: selectedDoctor.id,
            doctorname: selectedDoctor.name,
            education: selectedDoctor.doctorEducation ? [selectedDoctor.doctorEducation] : [],
            gender: orderDetails.orderedForGender || orderDetails.patientGender || "",
            hospital: selectedDoctor.hospitalName || "dandarHospital",
            image: selectedDoctor.profileImage || "",
            is_Ia: "false",
            name: orderDetails.orderedForName || orderDetails.patientName || "",
            patientId: orderDetails.orderedFor || orderDetails.patientId || "",
            patientprofile: orderDetails.orderForImage || orderDetails.patientImage || "",
            roomStatus: "IDLE",
            specialization: selectedDoctor.doctorSpecialization || "",
            status: "In Consultation",
          };

          await setDoc(docRef, payloadFirebase);
        }

        toast.success("Doctor assigned successfully!");
        onSuccess();
      } else {
        toast.error(response.message || "Failed to assign doctor");
      }
    } catch (error) {
      console.error("Firebase/Assign error:", error);
      toast.error("Failed to assign doctor");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Select Doctor */}
      <div>
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Doctor Details</label>
        {selectedDoctor ? (
          <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              {selectedDoctor.profileImage ? (
                <img src={selectedDoctor.profileImage} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500">
                  <User size={18} />
                </div>
              )}
              <div>
                <div className="text-xs font-bold text-slate-800">{selectedDoctor.name}</div>
                <div className="text-[0.65rem] text-slate-400">{selectedDoctor.mobileNumber}</div>
              </div>
            </div>
            <button
              className="text-xs text-indigo-500 font-semibold bg-transparent border-none cursor-pointer hover:text-indigo-700"
              onClick={() => setShowDoctorModal(true)}
            >
              Change
            </button>
          </div>
        ) : (
          <button
            className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-xs font-semibold text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors cursor-pointer bg-white flex items-center justify-center gap-1.5"
            onClick={() => setShowDoctorModal(true)}
          >
            <Plus size={14} /> Add Doctor
          </button>
        )}
      </div>

      {/* Patient Details */}
      <div>
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Patient Details</label>
        <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
          {orderDetails.orderForImage && (
            <img src={orderDetails.orderForImage} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
          )}
          <div>
            <div className="text-xs font-bold text-slate-800">{orderDetails.orderedForName}</div>
            <div className="text-[0.65rem] text-slate-400">
              {orderDetails.orderedForPhone}
            </div>
          </div>
        </div>
      </div>

      {/* Assign Button */}
      <div className="flex justify-end">
        <button
          className="px-5 py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all border-none cursor-pointer disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none flex items-center gap-2"
          disabled={!selectedDoctor || assigning}
          onClick={handleAssign}
        >
          {assigning && <Loader2 size={14} className="animate-spin" />}
          Assign Doctor
        </button>
      </div>

      {/* Doctor Selection Modal */}
      {showDoctorModal && (
        <DoctorSelectModal
          onClose={() => setShowDoctorModal(false)}
          onSelect={(doctor) => {
            setSelectedDoctor(doctor);
            setShowDoctorModal(false);
          }}
          doctors={doctors}
          loading={loading}
        />
      )}
    </div>
  );
};

export default DoctorAssignFlow;
